import React, {ReactNode, Suspense, useState} from 'react';
import {StyledEngineProvider} from '@mui/joy/styles';
import {SnackbarProvider} from 'notistack';
import {observer} from 'mobx-react-lite';


import '../figma-variables/palette.css';
import '../figma-variables/light.css';
import '../figma-variables/dark.css';
import '../figma-variables/text.css';


import './common.css';
import './Snackbar.css';


import {Layout} from './ui/elements/Layout';

import {SnackbarBlock} from "./ui/components/SnackbarBlock";
import {AuthModalsManager} from "./ui/screens/modals/auth/AuthModalsManager";
import { useRootStore, useUserState } from "@stores/AppStateContext";
import {DemoPanel} from './ui/components/DemoPanel/DemoPanel';
import {DeviceFrame} from './ui/components/DemoPanel/DeviceFrame';
import {DevicePreset, DEVICE_PRESETS} from './ui/components/DemoPanel/devicePresets';

const IS_DEMO = import.meta.env.VITE_BACKEND_TYPE === 'mock';
// Tablet/mobile preview loads this same app in a same-origin iframe (see
// DeviceFrame) — ScreenSizeProvider reads window.innerWidth, so only a real
// nested window can fake a breakpoint, a CSS-resized container can't. This
// param tells that inner instance it's the framed one, so it renders normally
// instead of nesting another device switcher inside itself.
const isFramedPreview = IS_DEMO && new URLSearchParams(window.location.search).has('frame');


const Theme: React.FunctionComponent<{ children: ReactNode }> = observer(({children}) => {
    const rootStore = useRootStore();
    const userState = useUserState();
    
    return (
        <div
            key={userState?.user?.["custom:clientId"]}
            className={rootStore.darkTheme ? 'theme-dark' : ''}>{children}</div>
    )
});


/*
* z-index levels:
* 5000 - snackbar
* 4750 - tooltips
* 4500 - context menu
* 4000 - modal windows
* 3000 - settings menu overlay
* 1000 - header menu
* 500 - context menu in Editor
*/
function App() {
    const [device, setDevice] = useState<DevicePreset>('desktop');
    // The framed preview instance always renders normally at desktop layout —
    // it's the iframe's own real window that's small, not this component.
    const showDevicePreview = IS_DEMO && !isFramedPreview && device !== 'desktop';

    return (
        <SnackbarProvider
            classes={{
                containerRoot: 'SnackbarRoot',
                root: 'Snackbar',
            }}
            Components={{
                default: SnackbarBlock,
                info: SnackbarBlock,
                error: SnackbarBlock,
                warning: SnackbarBlock,
                success: SnackbarBlock,
                // @ts-ignore
                primary: SnackbarBlock,
                ai: SnackbarBlock,
            }}
            anchorOrigin={{vertical: "bottom", horizontal: "center"}}
        >
            <StyledEngineProvider injectFirst>
                <Theme>
                    {/* display:contents keeps these full participants in Theme's layout when
                        shown, and fully hidden (but still mounted — no state loss, no reload)
                        when a device preview is showing instead. */}
                    <div style={{display: showDevicePreview ? 'none' : 'contents'}}>
                        <Layout/>
                        <AuthModalsManager/>
                    </div>
                    {showDevicePreview && (
                        <div className="App__devicePreview">
                            <DeviceFrame
                                width={DEVICE_PRESETS[device].width}
                                height={DEVICE_PRESETS[device].height}
                                label={DEVICE_PRESETS[device].label}
                                src={`${window.location.pathname}${window.location.search ? window.location.search + '&' : '?'}frame=1`}
                            />
                        </div>
                    )}
                    {IS_DEMO && !isFramedPreview && (
                        <DemoPanel device={device} onDeviceChange={setDevice}/>
                    )}
                </Theme>
            </StyledEngineProvider>
        </SnackbarProvider>
    );
}

export default App;
